import React, { useState, useCallback, useMemo } from "react";
import { notification, Icon, Modal } from "antd";
import { withRouter } from "react-router";
import styled from "styled-components";

import { getAuthorization } from "@arachnys/flow-app/src/api";
import Button from "@arachnys/styleguide/Button";
import { statuses } from "./config";

const ButtonContainer = styled.div`
    display: inline-block;
    margin-right: 10px;
`;

const ConfirmTextContainer = styled.div`
    display: flex;
    align-items: center;
`;

const DeleteConfirmIcon = styled.span`
    color: #f5222d;
    font-size: 24px;
    margin-right: 15px;
`;

const ConfirmButtonsContainer = styled.div`
    text-align: right;
    margin-top: 15px;
`;

const ChangeStatusButton = ({
    children,
    action,
    isAccept,
    requestId,
    setBusy = () => {},
    history,
    disabled,
    deleteConfirm = false,
    onSuccess = false,
    icon = false,
    filesQty = {},
}) => {
    const [showConfirm, setConfirm] = useState(false);

    const makeRequest = useCallback(async () => {
        setBusy(true);
        const auth = await getAuthorization();
        const requestBody = new FormData();
        requestBody.append("status", action);
        const request = new Request(
            `/api/tickets/bulk_upload_request/${requestId}/status/`,
            {
                method: "POST",
                headers: {
                    Authorization: auth,
                },
                body: requestBody,
            }
        );
        const result = await fetch(request);
        setBusy(false);
        if (result.ok) {
            if (onSuccess) {
                onSuccess();
            } else {
                history.push("/tickets/bulk/");
            }
        } else {
            notification.error({
                message: "Status change error",
                description:
                    "Sorry, you can not change request status at the moment.",
            });
        }
    }, [setBusy, action, requestId, onSuccess, history]);

    const onClick = useCallback(() => {
        if (deleteConfirm) {
            setConfirm(true);
        } else {
            if (action === statuses.ACCEPTED && !filesQty.SANITIZED) {
                notification.error({
                    message: "Please upload a file",
                    description:
                        "You need to upload at least one sanitized file to proceed",
                });
            } else if (action === statuses.COMPLETED && !filesQty.REPORT) {
                notification.error({
                    message: "Please upload a file",
                    description:
                        "You need to upload at least one report file to proceed",
                });
            } else {
                makeRequest();
            }
        }
    }, [
        deleteConfirm,
        action,
        filesQty.SANITIZED,
        filesQty.REPORT,
        makeRequest,
    ]);

    const CustomButton = useMemo(
        () => props =>
            icon ? (
                <Icon type={icon} {...props} />
            ) : isAccept ? (
                <Button.Primary {...props} />
            ) : (
                <Button.Alert {...props} />
            ),
        [isAccept, icon]
    );

    return (
        <>
            <ButtonContainer>
                <CustomButton onClick={onClick} disabled={disabled}>
                    {children}
                </CustomButton>
            </ButtonContainer>
            {deleteConfirm && (
                <Modal
                    visible={showConfirm}
                    onCancel={() => setConfirm(false)}
                    title={null}
                    footer={null}
                >
                    <ConfirmTextContainer>
                        <DeleteConfirmIcon>
                            <Icon type="close-circle" />
                        </DeleteConfirmIcon>
                        Delete Request?
                    </ConfirmTextContainer>
                    <ConfirmButtonsContainer>
                        <ButtonContainer>
                            <Button.Secondary
                                onClick={() => setConfirm(false)}
                                reverse
                            >
                                Cancel
                            </Button.Secondary>
                        </ButtonContainer>
                        <ButtonContainer>
                            <Button.Alert onClick={makeRequest}>
                                Delete
                            </Button.Alert>
                        </ButtonContainer>
                    </ConfirmButtonsContainer>
                </Modal>
            )}
        </>
    );
};

export default withRouter(ChangeStatusButton);
